import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { useLlmModels } from "@/hooks/useLlmModels";
import { cn } from "@/lib/utils";
import { NewMedicalTextDialog } from "@/test-data/NewMedicalTextDialog";
import { getMastraRunUrl } from "@/ai-coding-outputs/mastraWorkflowRunUrl";
import type { ClassifyFieldResult } from "@/types/classification";
import dayjs from "dayjs";
import { AlertCircle, ExternalLink, History, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { RunResultWithStages, RunStage } from "../useRunResultWithStages";
import CoMorbiditiesStepResultCard from "./CoMorbiditiesStepResultCard";
import DebugInfoJsonViewer from "./DebugInfoJsonViewer";
import { ExpandableCodeChip } from "./ExpandableCodeChip";
import { ExpandableDiagnosisCodeChip } from "./ExpandableDiagnosisCodeChip";
import { FieldResult } from "./FieldResult";
import OrderDiagnosisStepResultCard from "./OrderDiagnosisStepResultCard";
import OrderProcedureStepResultCard from "./OrderProcedureStepResultCard";
import PersonalHistoryStepResultCard from "./PersonalHistoryStepResultCard";
import FollowUpStepResultCard from "./FollowUpStepResultCard";
import { ReClassifyDialog } from "./ReClassifyDialog";
import SymptomStepResultCard from "./SymptomStepResultCard";
import DiagnosesBrowserStepResultCard from "./DiagnosesBrowserStepResultCard";
import { getAllDiagnoses, getAllProcedures } from "./normaliseCodesForDisplay";
import useRegressions from "./useRegressions";

interface RunStageDetailsProps {
  run: RunResultWithStages;
  stageKey: string;
  stageResult: RunStage;
}

const RunStageDetails = ({ run, stageKey, stageResult }: RunStageDetailsProps) => {
  const navigate = useNavigate();
  const [selectedTemplateField, setSelectedTemplateField] = useState<ClassifyFieldResult | null>(null);
  const [medicalTextDialogOpen, setMedicalTextDialogOpen] = useState(false);

  const { data: llmModels } = useLlmModels();

  const {
    lastSuccessQuery: { data: lastSuccessfulRun },
    regressionsQuery: { data: regressions },
  } = useRegressions({
    runId: run.id,
    runEndTime: run.run_end_time,
  });

  if (!stageResult.result) return null;

  const procedures = getAllProcedures({
    expectedProcedures: stageResult.test_data.expected_procedures.map((proc) => ({
      code: proc.opcs4_codes?.code ?? "",
      description: proc.opcs4_codes?.description ?? "",
      id: proc.code_id ?? "",
      type: "OPCS-4" as const,
    })),
    runProcedures: stageResult.result.procedures || [],
  });

  // Combine Diagnoses and Co-morbidities into a single comparison list
  const combinedDiagnoses = getAllDiagnoses({
    expectedDiagnoses: [
      ...stageResult.test_data.expected_diagnosis.map((diag) => ({
        ...diag.icd10_codes,
        id: diag.code_id,
        isOptional: !!diag.is_optional,
        type: "ICD-10" as const,
      })),
      ...stageResult.test_data.expected_co_morbidities.map((comorb) => ({
        ...comorb.icd10_codes,
        id: comorb.code_id,
        isOptional: !!comorb.is_optional,
        type: "ICD-10" as const,
      })),
    ],
    runDiagnoses: [
      ...(stageResult.result.diagnoses || []),
      ...(stageResult.result.coMorbidities || []),
      ...(stageResult.result.personalHistoryDiagnoses || []),
    ],
  });

  return (
    <>
      {selectedTemplateField && (
        <ReClassifyDialog
          open={!!selectedTemplateField}
          onClose={() => setSelectedTemplateField(null)}
          testDataId={stageResult.test_data_id}
          onReClassifyComplete={() => {
            // Optional callback for when re-classification completes
            setSelectedTemplateField(null);
          }}
        />
      )}
      <NewMedicalTextDialog
        open={medicalTextDialogOpen}
        onClose={() => setMedicalTextDialogOpen(false)}
        medicalTextId={stageResult.test_data_id}
      />
      <div className="h-[calc(100vh-4rem)] flex">
        {/* Left column */}
        <div className="w-full max-w-md border-r h-full overflow-y-auto p-2">
          <Card className="rounded-md border bg-muted/30 mb-6">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-block px-2 py-1 rounded-full text-sm",
                      stageKey === "endoscopyStageResult"
                        ? "bg-blue-100 text-blue-800"
                        : stageKey === "histologyStageResult"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800",
                    )}
                  >
                    {stageKey === "endoscopyStageResult"
                      ? "Endoscopy"
                      : stageKey === "histologyStageResult"
                        ? "Histology"
                        : "GP Summary"}
                  </span>
                  {stageResult.test_data.label ? (
                    <span className="text-muted-foreground text-sm">({stageResult.test_data.label})</span>
                  ) : null}
                  <span className="text-muted-foreground text-sm">({stageResult.test_data.sequence_number})</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setMedicalTextDialogOpen(true)}
                  title="View/Edit Medical Text Example"
                  aria-label="View/Edit Medical Text Example"
                >
                  <Settings className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <MarkdownRenderer
                content={stageResult.test_data.text}
                className="bg-background rounded p-2 overflow-x-auto max-w-full"
              />
            </CardContent>
          </Card>
        </div>
        {/* Right column */}
        <div className="flex-1 h-full overflow-auto p-6 space-y-6 max-w-full">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-4">
                Run Details
                {run.run_end_time && (
                  <span className="text-base font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full ml-2">
                    {dayjs(run.run_start_time).tz().format("DD/MM HH:mm")}
                  </span>
                )}
                {regressions && regressions.length > 0 && (
                  <span className="flex items-center gap-1 text-base font-normal bg-red-100 text-red-800 px-3 py-1 rounded-full ml-2">
                    <AlertCircle className="h-4 w-4 mr-1 text-red-600" />
                    Regression
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Go to last successful run"
                      aria-label="Go to last successful run"
                      disabled={!lastSuccessfulRun}
                      onClick={() => {
                        if (lastSuccessfulRun) {
                          navigate(`/test-data/runs/${lastSuccessfulRun.id}`);
                        }
                      }}
                    >
                      <History className="h-5 w-5" />
                    </Button>
                  </span>
                )}
                <span className="text-base font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full ml-2">
                  {llmModels
                    ? llmModels.find((model) => model.modelId === run.llm_model_id)?.name || "Unknown"
                    : "Loading model..."}
                </span>
              </h1>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={getMastraRunUrl("testEndoscopy", run.endoscopyStageResult?.workflow_run_id ?? "")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">View Run</span>
                  </a>
                </Button>
              </div>
              {run.error_message && (
                <div className="text-red-500 mt-2">
                  <p className="font-medium">There was a top level error preventing classification:</p>
                  <pre className="text-sm bg-muted p-2 rounded mt-1 whitespace-pre-wrap overflow-x-auto max-w-full break-words">
                    {run.error_message}
                  </pre>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* AI Flag For Review Card */}
            <div className="rounded-md border p-4 w-full max-w-full">
              <details>
                <summary className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="font-medium">AI Flag For Review:</span>
                  <span
                    className={
                      stageResult.result.aiFlagForReview ? "text-red-600 font-bold" : "text-green-600 font-bold"
                    }
                  >
                    {stageResult.result.aiFlagForReview ? "True" : "False"}
                  </span>
                  {stageResult.result.aiFlagForReview && <span className="ml-2">🤖</span>}
                </summary>
                {stageResult.result.aiFlagForReview && stageResult.result.aiFlagCheckStepResult && (
                  <div className="mt-4">
                    <div>
                      <div className="font-semibold mb-2">Reasons</div>
                      <div className="text-sm text-muted-foreground">
                        {stageResult.result.aiFlagCheckStepResult.reasons.join(". ")}
                      </div>
                    </div>
                    <DebugInfoJsonViewer debugInfo={stageResult.result.aiFlagCheckStepResult.debugInfo} />
                  </div>
                )}
              </details>
            </div>

            {stageResult.result.followUpStepResult?.isFollowUp ? (
              <div className="rounded-md border p-4 w-full max-w-full">
                <details>
                  <summary className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="font-medium">Is Follow-Up:</span>
                    <span
                      className={
                        stageResult.result.followUpStepResult?.isFollowUp
                          ? "text-red-600 font-bold"
                          : "text-gray-600 font-bold"
                      }
                    >
                      {stageResult.result.followUpStepResult?.isFollowUp ? "True" : "False"}
                    </span>
                    {stageResult.result.followUpStepResult?.isFollowUp && <span className="ml-2">🙅</span>}
                  </summary>
                  {stageResult.result.followUpStepResult?.isFollowUp &&
                    stageResult.result.followUpStepResult?.debugInfo && (
                      <div className="mt-4">
                        <div className="font-semibold mb-2">Is Follow-Up Debug Info</div>
                        <DebugInfoJsonViewer debugInfo={stageResult.result.followUpStepResult?.debugInfo} />
                      </div>
                    )}
                </details>
              </div>
            ) : (
              <>
                <div className="rounded-md border p-4 space-y-4 w-full max-w-full">
                  <h2 className="text-lg font-medium mb-2">Procedures</h2>
                  <div className="flex flex-col gap-2">
                    {procedures.map((proc, index) => (
                      <ExpandableCodeChip
                        key={proc.id || `${proc.code}-${index}`}
                        codeId={proc.id}
                        code={proc.code}
                        description={proc.description}
                        isExpected={proc.isExpected}
                        isPresent={proc.isPresent}
                        isPositionCorrect={proc.isPositionCorrect}
                      />
                    ))}
                    {procedures.length === 0 && <div className="text-muted-foreground">No procedures found</div>}
                  </div>
                  <div>
                    {stageResult.result.orderProceduresStepResult && (
                      <OrderProcedureStepResultCard result={stageResult.result.orderProceduresStepResult} />
                    )}
                  </div>
                </div>

                <div className="rounded-md border p-4 space-y-4 w-full max-w-full">
                  <h2 className="text-lg font-medium mb-2">Diagnoses</h2>
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                      {combinedDiagnoses.map((diagnosis, index) => (
                        <ExpandableDiagnosisCodeChip
                          key={diagnosis.id || diagnosis.code}
                          codeId={diagnosis.id}
                          code={diagnosis.code}
                          description={diagnosis.description}
                          isExpected={diagnosis.isExpected}
                          isPresent={diagnosis.isPresent}
                          isPositionCorrect={diagnosis.isPositionCorrect}
                          isOptional={diagnosis.isOptional ?? false}
                          isPrimary={index === 0}
                        />
                      ))}
                    </div>
                    {stageResult.result.followUpStepResult && (
                      <FollowUpStepResultCard result={stageResult.result.followUpStepResult} />
                    )}
                    {stageResult.result.orderDiagnosisStepResult && (
                      <OrderDiagnosisStepResultCard
                        result={stageResult.result.orderDiagnosisStepResult}
                        diagnoses={stageResult.result.templateDiagnoses ?? []}
                        medicalTestDataId={stageResult.test_data_id}
                        llmModelId={run.llm_model_id}
                      />
                    )}
                    {stageResult.result.diagnosesBrowserStepResult && (
                      <DiagnosesBrowserStepResultCard result={stageResult.result.diagnosesBrowserStepResult} />
                    )}
                    {stageResult.result.symptomStepResult && (
                      <SymptomStepResultCard result={stageResult.result.symptomStepResult} />
                    )}
                    {stageResult.result.coMorbiditiesStepResult && (
                      <div className="mt-2">
                        <CoMorbiditiesStepResultCard
                          result={stageResult.result.coMorbiditiesStepResult}
                          medicalText={stageResult.test_data.text}
                          diagnoses={(stageResult.result.coMorbidities ?? []).map((d) => d.code)}
                          llmModelId={run.llm_model_id}
                        />
                      </div>
                    )}
                    {stageResult.result.personalHistoryStepResult && (
                      <PersonalHistoryStepResultCard result={stageResult.result.personalHistoryStepResult} />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            {(stageResult.result.fieldClassificationResults || []).map((fieldResult) => (
              <FieldResult
                key={fieldResult.fieldId}
                result={fieldResult}
                medicalTestDataId={stageResult.test_data_id}
                onReClassify={() => setSelectedTemplateField(fieldResult)}
              />
            ))}
            {(stageResult.result.fieldClassificationResults || []).length === 0 && (
              <div className="text-center text-muted-foreground p-8 border rounded-lg">
                No classification results available
              </div>
            )}
            {stageResult.result.error && (
              <div className="rounded-md border-2 border-red-500 bg-red-50 p-4">
                <div className="font-medium text-red-800 mb-1">Error</div>
                <div className="text-sm text-red-600 whitespace-pre-wrap break-words">{stageResult.result.error}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RunStageDetails;
